import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, defer, map, of, switchMap, takeUntil } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import * as NotificationActions from '../actions/notification.actions';
import { NotificationsService } from '../services/notification.service';

@Injectable()
export class NotificationsEffects {
  private notificationsService = inject(NotificationsService);

  watchActiveRecordingNotification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationActions.watchActiveRecordingNotification),
      switchMap(({ recordingId, userId }) =>
        this.notificationsService
          .watchActiveRecordingNotification(recordingId, userId)
          .pipe(
            map((notification) =>
              NotificationActions.watchActiveRecordingNotificationSuccess({
                recordingId,
                notification,
              })
            ),
            catchError((error) =>
              of(
                NotificationActions.watchActiveRecordingNotificationFailure({ error })
              )
            ),
            takeUntil(
              this.actions$.pipe(
                ofType(NotificationActions.stopWatchingActiveRecordingNotification)
              )
            )
          )
      )
    );
  });

  markRecordingNotificationRead$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationActions.markRecordingNotificationRead),
      mergeMap(({ recordingId, userId, read }) =>
        defer(() =>
          this.notificationsService.markRecordingNotificationRead(
            recordingId,
            userId,
            read ?? true
          )
        ).pipe(
          map(() =>
            NotificationActions.markRecordingNotificationReadSuccess({
              recordingId,
              userId,
              read,
            })
          ),
          catchError((error) =>
            of(
              NotificationActions.markRecordingNotificationReadFailure({
                recordingId,
                read,
                error,
              })
            )
          )
        )
      )
    );
  });

  watchNotificationsForRecordingsUnread$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationActions.watchNotificationsForRecordingsUnread),
      mergeMap(({ recordingIds, userId }) =>
        this.notificationsService
          .watchNotificationsForRecordingsUnread(recordingIds, userId)
          .pipe(
            map((notifications) =>
              NotificationActions.watchNotificationsForRecordingsUnreadSuccess(
                { recordingIds, notifications }
              )
            ),
            catchError((error) =>
              of(
                NotificationActions.watchNotificationsForRecordingsUnreadFailure(
                  { error }
                )
              )
            ),
            takeUntil(
              this.actions$.pipe(
                ofType(
                  NotificationActions.stopWatchingNotificationsForRecordingsUnread
                )
              )
            )
          )
      )
    );
  });

  watchUnreadNotificationCountsByHorse$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NotificationActions.watchUnreadNotificationCountsByHorse),
      mergeMap(({ horseIds, userId }) =>
        this.notificationsService
          .watchUnreadNotificationCountsForHorses(horseIds, userId)
          .pipe(
            map((unreadNotificationCountsByHorse) =>
              NotificationActions.watchUnreadNotificationCountsByHorseSuccess({
                horseIds,
                unreadNotificationCountsByHorse,
              })
            ),
            catchError((error) =>
              of(
                NotificationActions.watchUnreadNotificationCountsByHorseFailure({
                  error,
                })
              )
            ),
            takeUntil(
              this.actions$.pipe(
                ofType(
                  NotificationActions.stopWatchingUnreadNotificationCountsByHorse
                )
              )
            )
          )
      )
    );
  });

  constructor(private actions$: Actions) {}
}
